import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import api from './api';
import qs from 'qs';
import BusyOverlay from './BusyOverlay';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';

const MyClubIsOnSnapperish = () => {
  const theme = useTheme();
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const data = {
      userEmail: email,
      captchaToken: recaptchaValue,
    };

    try {
      setIsLoading(true);
      const res = await api.post('/getlogin', qs.stringify(data), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      setIsLoading(false);

      if (res.status === 200) {
        setSuccess(true);
      } else {
        setError(res.statusText || 'Unexpected Response');
      }
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data || 'Request failed');
      console.error('Login request error:', err.response?.data || err.message);
    }
  };

  const isFormValid = email && recaptchaValue && acceptedTerms;

  if (success) {
    return (
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: 'bold' }}>
            Check Your Email!
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: 2 }}>
            We've sent you a special login link. Check your email and click the link to login without a password.
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            If you don't see an email from us, check your spam folder and check with your club to make sure they've added this email address as a member.
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 3 }}>
            After logging in, you can create or update a password by clicking your profile icon and selecting Settings.
          </Typography>
          <Link
            to="/login"
            style={{
              textDecoration: 'none',
              color: theme.palette.primary.main,
            }}
          >
            Back to Login
          </Link>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <BusyOverlay open={isLoading} message="Sending login link..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: 2 }}>
              MY CLUB IS ON SNAPPERISH
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Enter your email address to receive a login link
            </Typography>
            <Link
              to="/login"
              style={{
                textDecoration: 'none',
                color: theme.palette.text.primary,
              }}
            >
              Back to Login
            </Link>
          </Box>
          {error && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <GoogleSignInButton />
          </Box>
          <Box sx={{ mt: 1 }}>
            <AppleSignInButton />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>or get a magic link</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            }}
          >
            <TextField
              required
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                  I agree to the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Terms of Use
                  </Link>, {' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Privacy Policy
                  </Link>, and {' '}
                  <Link
                    to="/acceptableuse"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Acceptable Use Policy
                  </Link>
                </Typography>
              }
              sx={{
                alignItems: 'flex-start',
                marginTop: 1,
                marginBottom: 1,
                '& .MuiFormControlLabel-label': {
                  marginTop: '2px'
                }
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey="6LefeGYrAAAAALRTOHKFlSdFzx9cSnpE6Nt52gcC"
                onChange={(value) => setRecaptchaValue(value)}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              style={{ color: theme.palette.primary.contrastText }}
            >
              SEND LOGIN LINK
            </Button>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default MyClubIsOnSnapperish;